import React from 'react';
import {Box, Rating, Typography} from '@mui/material';
import {RatingQuestion} from 'crqlar-react-common/dist/models/forms/form';
import EditableTypography from 'crqlar-react-common/dist/components/EditableTypography';
import StarIcon from '@mui/icons-material/Star';
import MdEditor from 'crqlar-react-common/dist/components/MdEditor';
import {useTranslation} from 'react-i18next';

interface RatingEditorProps {
    question: RatingQuestion;
    onQuestionUpdate: (question: RatingQuestion) => void;
}

const RatingEditor: React.FC<RatingEditorProps> = ({
                                                         question,
                                                         onQuestionUpdate
                                                     }) => {
    const {t} = useTranslation();

    const handleTitleUpdate = (title: string) => {
        onQuestionUpdate({
            ...question,
            title
        });
    };

    const handleDescriptionUpdate = (description?: string) => {
        onQuestionUpdate({
            ...question,
            description: description || ''
        });
    };

    const handleLabelUpdate = (ratingLabel: string) => {
        onQuestionUpdate({
            ...question,
            ratingLabel
        });
    };

    const maxRating = question.maxRating || 10;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                p: 2,
                alignItems: 'center',
            }}
        >
            {/* Title */}
            <EditableTypography
                value={question.title}
                onChange={handleTitleUpdate}
                multiline
                variant="h6"
                sx={{
                    mb: 2,
                    width: '100%',
                    fontWeight: 'medium',
                    textAlign: 'center',
                    '& input': {
                        textAlign: 'center',
                    },
                    padding: '8px',
                    borderRadius: 1,
                }}
                placeholder={t('Rate your experience')}
            />

            {/* Description */}
            <Box sx={{mb: 3, width: '100%'}}>
                <MdEditor
                    value={question.description || ''}
                    onChange={handleDescriptionUpdate}
                    placeholder={t('Question description (optional)')}
                    className={'mini-mdx'}
                />
            </Box>

            {/* Optional Rating Label */}
            {question.ratingLabel && (
                <EditableTypography
                    value={question.ratingLabel}
                    onChange={handleLabelUpdate}
                    variant="body2"
                    sx={{
                        mb: 3,
                        width: '100%',
                        textAlign: 'center',
                        color: 'text.secondary',
                        '& input': {
                            textAlign: 'center',
                        },
                        padding: '4px',
                        borderRadius: 1,
                    }}
                    placeholder={t('Rating label (e.g., Energy Level)')}
                />
            )}

            {/* Rating Display */}
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                {maxRating === 5 ? (
                    <Rating
                        value={0}
                        max={5}
                        size="large"
                        readOnly
                        emptyIcon={<StarIcon style={{opacity: 0.3}} fontSize="inherit" />}
                    />
                ) : (
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center'}}>
                        {Array.from({length: maxRating}, (_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    color: 'text.secondary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'action.hover'
                                    }
                                }}
                            >
                                {i + 1}
                            </Box>
                        ))}
                    </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                    {maxRating === 5 ? t('1-5 Star Rating') : t('1-{{maxRating}} Scale', {maxRating})}
                </Typography>
            </Box>
        </Box>
    );
};

export default RatingEditor;
